import * as XLSX from "xlsx";

// ---- Types ----

export interface ParsedSkuRow {
  /** Row number from the original Excel (1-indexed, excluding header) */
  rowNum: number;
  code: string;
  name: string;
  category: string;
  unit: string;
  weightKg: number;
  nipPaise: number;
  pricePaise: number;
  mrpPaise: number;
  marginPercent: number;
  gstPercent: number;
  availableQty: number;
  brand?: string;
  description?: string;
  origin?: string;
  packagingType?: string;
  shelfLife?: string;
  moq?: number;
  errors: string[];
  warnings: string[];
}

export interface ValidationResult {
  totalRows: number;
  validRows: number;
  errorRows: number;
  warningRows: number;
  errors: Array<{ row: number; message: string }>;
  warnings: Array<{ row: number; message: string }>;
}

export interface SkuExportRow {
  id: number | null;
  code: string;
  name: string;
  category?: string | null;
  unit?: string | null;
  weightKg?: number | null;
  nipPaise?: number | null;
  pricePaise: number;
  mrpPaise?: number | null;
  marginPercent?: number | null;
  gstPercent?: number | null;
  availableQty: number;
  active: boolean;
  brand?: string | null;
  description?: string | null;
  origin?: string | null;
  packagingType?: string | null;
  shelfLife?: string | null;
  moq?: number | null;
}

// ---- Column Mapping ----

const HEADER_MAP: Record<string, keyof ParsedSkuRow> = {
  "sku code": "code",
  "sku_code": "code",
  "skucode": "code",
  code: "code",
  "product name": "name",
  "product_name": "name",
  productname: "name",
  name: "name",
  category: "category",
  unit: "unit",
  "pack unit": "unit",
  "pack_unit": "unit",
  weight: "weightKg",
  "weight (kg)": "weightKg",
  "weight kg": "weightKg",
  "weight_kg": "weightKg",
  weightkg: "weightKg",
  "net weight": "weightKg",
  "bag weight": "weightKg",
  "pack weight": "weightKg",
  nip: "nipPaise",
  "nip (₹)": "nipPaise",
  "nip(next inward price)": "nipPaise",
  "nip (next inward price)": "nipPaise",
  "next inward price": "nipPaise",
  "next inward price (₹)": "nipPaise",
  "landing price": "nipPaise",
  "landing price (₹)": "nipPaise",
  "our landing price": "nipPaise",
  "inward price": "nipPaise",
  "inward price (₹)": "nipPaise",
  "nip_paise": "nipPaise",
  "trade price": "pricePaise",
  "trade price (₹)": "pricePaise",
  "trade_price": "pricePaise",
  "wholesale price": "pricePaise",
  "wholesale price (₹)": "pricePaise",
  "wholesale_price": "pricePaise",
  "selling price": "pricePaise",
  "selling price (₹)": "pricePaise",
  "selling price - price on app": "pricePaise",
  "price on app": "pricePaise",
  "price on app (₹)": "pricePaise",
  "app price": "pricePaise",
  "price": "pricePaise",
  "price (₹)": "pricePaise",
  "mrp": "mrpPaise",
  "mrp (₹)": "mrpPaise",
  "mrp_paise": "mrpPaise",
  "mdd margin": "marginPercent",
  "mdd margin (₹/kg)": "marginPercent",
  "mdd margin (rs/kg)": "marginPercent",
  "mdd margin rs/kg": "marginPercent",
  "mdd margin/kg": "marginPercent",
  "margin (₹/kg)": "marginPercent",
  "margin (rs/kg)": "marginPercent",
  "margin rs/kg": "marginPercent",
  "margin/kg": "marginPercent",
  "margin per kg": "marginPercent",
  "margin per kg (₹)": "marginPercent",
  "per kg margin": "marginPercent",
  "margin %": "marginPercent",
  "margin%": "marginPercent",
  "margin (%)": "marginPercent",
  "margin(%)": "marginPercent",
  "margin_percent": "marginPercent",
  "marginpercent": "marginPercent",
  margin: "marginPercent",
  "retailer margin": "marginPercent",
  "retailer margin (%)": "marginPercent",
  "retailer margin %": "marginPercent",
  "profit margin": "marginPercent",
  "margin percentage": "marginPercent",
  "gst": "gstPercent",
  "gst %": "gstPercent",
  "gst%": "gstPercent",
  "gst (%)": "gstPercent",
  "gst(%)": "gstPercent",
  "gst_percent": "gstPercent",
  "gstpercent": "gstPercent",
  "gst rate": "gstPercent",
  "tax": "gstPercent",
  "tax %": "gstPercent",
  "tax (%)": "gstPercent",
  "stock qty": "availableQty",
  "stock_qty": "availableQty",
  "available qty": "availableQty",
  stock: "availableQty",
  quantity: "availableQty",
  brand: "brand",
  "brand name": "brand",
  "brand_name": "brand",
  description: "description",
  "product description": "description",
  "product_description": "description",
  desc: "description",
  origin: "origin",
  "country of origin": "origin",
  "mandi origin": "origin",
  mandi: "origin",
  packaging: "packagingType",
  "packaging type": "packagingType",
  "packaging_type": "packagingType",
  "pack type": "packagingType",
  "shelf life": "shelfLife",
  shelflife: "shelfLife",
  "shelf_life": "shelfLife",
  "best before": "shelfLife",
  moq: "moq",
  "minimum order qty": "moq",
  "min order qty": "moq",
};

// ---- Calculation Helpers ----

/**
 * Infers weight in KG from product name or unit (e.g. "GTS RICE 30KG" -> 30, "500g" -> 0.5).
 * Defaults to 1 kg if no explicit weight pattern is found.
 */
export function inferWeightKg(
  name?: string | null,
  unit?: string | null
): number {
  const combined = `${name ?? ""} ${unit ?? ""}`;
  const kgMatch = combined.match(/(\d+(?:\.\d+)?)\s*(?:kg|kgs|kilogram)/i);
  if (kgMatch) {
    const val = parseFloat(kgMatch[1]);
    if (!isNaN(val) && val > 0) return val;
  }
  const gmMatch = combined.match(/(\d+(?:\.\d+)?)\s*(?:g|gm|gms|gram)\b/i);
  if (gmMatch) {
    const val = parseFloat(gmMatch[1]);
    if (!isNaN(val) && val > 0) return parseFloat((val / 1000).toFixed(3));
  }
  return 1;
}

/**
 * Returns default MDD Margin in ₹/kg (0 by default; all pricing & margin commercials are entered from the console).
 */
export function getDefaultMddMarginPerKg(
  _unit?: string | null,
  _category?: string | null,
  _name?: string | null
): number {
  return 0;
}

/**
 * Selling Price (Price On App) = (Weight * Margin per KG) + NIP (Next Inward Price / Landing Price)
 */
export function calculateSellingPricePaise(
  nipPaise: number,
  weightKg: number,
  marginPerKgRupees: number
): number {
  if (nipPaise < 0) return 0;
  const effectiveWeight = weightKg > 0 ? weightKg : 1;
  const totalMarginPaise = Math.round(effectiveWeight * marginPerKgRupees * 100);
  return Math.max(0, nipPaise + totalMarginPaise);
}

/**
 * Back-calculates NIP (Next Inward Price / Landing Price) from Selling Price, Weight, and Margin per KG:
 * NIP = Selling Price - (Weight * Margin per KG)
 */
export function calculateNipPaise(
  sellingPricePaise: number,
  weightKg: number,
  marginPerKgRupees: number
): number {
  if (sellingPricePaise <= 0) return 0;
  const effectiveWeight = weightKg > 0 ? weightKg : 1;
  const totalMarginPaise = Math.round(effectiveWeight * marginPerKgRupees * 100);
  return Math.max(0, sellingPricePaise - totalMarginPaise);
}

/**
 * Legacy helper kept for compatibility; returns independent mrpPaise or 0.
 */
export function calculateMrpFromMarginAndGst(
  _pricePaise: number,
  _mddMarginRupeesPerKg: number,
  _gstRatePercent: number = 5
): number {
  return 0;
}

export function calculateMarginFromMrpAndGst(
  _pricePaise: number,
  _mrpPaise: number,
  _gstRatePercent: number = 5
): number {
  return 0;
}

// ---- Export ----

/**
 * Generates a downloadable .xlsx Blob from the current catalog.
 * By default (for regular Admins), NIP and Margin are strictly excluded.
 * Pass `includeInternalMargins = true` only for Co-Founder Super Admins.
 */
export function exportCatalogToXlsx(
  skus: SkuExportRow[],
  includeInternalMargins = false,
): Blob {
  const headers = includeInternalMargins
    ? [
        "SKU Code",
        "Product Name",
        "Category",
        "Unit",
        "Weight (kg)",
        "NIP (₹)",
        "Margin per KG (₹)",
        "Selling Price (₹)",
        "MRP (₹)",
        "GST %",
        "Stock Qty",
        "Active",
        "Brand",
        "Description",
        "Origin",
        "Packaging Type",
        "Shelf Life",
        "MOQ",
      ]
    : [
        "SKU Code",
        "Product Name",
        "Category",
        "Unit",
        "Weight (kg)",
        "Selling Price (₹)",
        "MRP (₹)",
        "GST %",
        "Stock Qty",
        "Active",
        "Brand",
        "Description",
        "Origin",
        "Packaging Type",
        "Shelf Life",
        "MOQ",
      ];

  const rows = skus.map((sku) => {
    const margin = sku.marginPercent ?? 0;
    const weightKg = sku.weightKg && sku.weightKg > 0 ? sku.weightKg : inferWeightKg(sku.name, sku.unit);
    const gst = sku.gstPercent !== undefined && sku.gstPercent !== null ? sku.gstPercent : 0;
    const nipPaise =
      sku.nipPaise !== undefined && sku.nipPaise !== null && sku.nipPaise > 0
        ? sku.nipPaise
        : calculateNipPaise(sku.pricePaise, weightKg, margin);
    const sellingPricePaise =
      sku.pricePaise > 0
        ? sku.pricePaise
        : calculateSellingPricePaise(nipPaise, weightKg, margin);
    const mrp = sku.mrpPaise && sku.mrpPaise > 0 ? sku.mrpPaise / 100 : "";

    if (includeInternalMargins) {
      return [
        sku.code,
        sku.name,
        sku.category ?? "",
        sku.unit ?? "",
        weightKg,
        nipPaise / 100,
        margin,
        sellingPricePaise / 100,
        mrp,
        gst,
        sku.availableQty,
        sku.active ? "Yes" : "No",
        sku.brand ?? "",
        sku.description ?? "",
        sku.origin ?? "",
        sku.packagingType ?? "",
        sku.shelfLife ?? "",
        sku.moq ?? 1,
      ];
    }

    return [
      sku.code,
      sku.name,
      sku.category ?? "",
      sku.unit ?? "",
      weightKg,
      sellingPricePaise / 100,
      mrp,
      gst,
      sku.availableQty,
      sku.active ? "Yes" : "No",
      sku.brand ?? "",
      sku.description ?? "",
      sku.origin ?? "",
      sku.packagingType ?? "",
      sku.shelfLife ?? "",
      sku.moq ?? 1,
    ];
  });

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  // Auto-width columns
  const colWidths = headers.map((h, i) => {
    const maxLen = Math.max(
      h.length,
      ...rows.map((r) => String(r[i]).length)
    );
    return { wch: Math.min(maxLen + 2, 30) };
  });
  ws["!cols"] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Catalog");

  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/**
 * Generates a blank template .xlsx with just the headers.
 */
export function generateTemplateXlsx(): Blob {
  return exportCatalogToXlsx([]);
}

// ---- Parse ----

/**
 * Parses an uploaded Excel or CSV file into ParsedSkuRow[].
 */
export async function parseCatalogXlsx(
  file: File
): Promise<ParsedSkuRow[]> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error("No sheets found in the uploaded file.");

  const ws = wb.Sheets[sheetName];
  const raw: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });

  if (raw.length < 2) {
    throw new Error(
      "The file must have a header row and at least one data row."
    );
  }

  // Map headers
  const headerRow = raw[0] as string[];
  const colMap: Record<number, keyof ParsedSkuRow> = {};
  headerRow.forEach((cell, idx) => {
    const normalized = String(cell ?? "")
      .trim()
      .toLowerCase();
    const mapped = HEADER_MAP[normalized];
    if (mapped) colMap[idx] = mapped;
  });

  // Parse data rows
  const parsed: ParsedSkuRow[] = [];
  for (let i = 1; i < raw.length; i++) {
    const row = raw[i] as unknown[];
    if (!row || row.every((c) => c === null || c === undefined || c === ""))
      continue;

    let hasExplicitGst = false;
    let hasExplicitMargin = false;
    let hasExplicitWeight = false;
    let hasExplicitNip = false;
    let hasExplicitPrice = false;

    const entry: ParsedSkuRow = {
      rowNum: i + 1,
      code: "",
      name: "",
      category: "",
      unit: "",
      weightKg: 1,
      nipPaise: 0,
      pricePaise: 0,
      mrpPaise: 0,
      marginPercent: 0,
      gstPercent: 0,
      availableQty: 0,
      errors: [],
      warnings: [],
    };

    for (const [colIdx, field] of Object.entries(colMap)) {
      const cellValue = row[Number(colIdx)];
      const strVal = String(cellValue ?? "").trim();

      switch (field) {
        case "code":
          entry.code = strVal;
          break;
        case "name":
          entry.name = strVal;
          break;
        case "category":
          entry.category = strVal;
          break;
        case "unit":
          entry.unit = strVal;
          break;
        case "weightKg": {
          if (strVal === "") break;
          const cleaned = strVal.replace(/[^\d.]/g, "").trim();
          const num = parseFloat(cleaned);
          if (isNaN(num) || num <= 0) {
            entry.warnings.push(`Invalid weight: "${strVal}", defaulting to 1 kg`);
          } else {
            entry.weightKg = parseFloat(num.toFixed(3));
            hasExplicitWeight = true;
          }
          break;
        }
        case "nipPaise": {
          if (strVal === "") break;
          const cleaned = strVal.replace(/[₹,]/g, "").trim();
          const num = parseFloat(cleaned);
          if (isNaN(num) || num < 0) {
            entry.errors.push(`Invalid NIP (Landing Price): "${strVal}"`);
          } else {
            entry.nipPaise = Math.round(num * 100);
            hasExplicitNip = true;
          }
          break;
        }
        case "pricePaise": {
          if (strVal === "") break;
          const cleaned = strVal.replace(/[₹,]/g, "").trim();
          const num = parseFloat(cleaned);
          if (isNaN(num) || num < 0) {
            entry.errors.push(`Invalid selling price: "${strVal}"`);
          } else {
            entry.pricePaise = Math.round(num * 100);
            hasExplicitPrice = true;
          }
          break;
        }
        case "mrpPaise": {
          if (strVal === "") break;
          const cleaned = strVal.replace(/[₹,]/g, "").trim();
          const num = parseFloat(cleaned);
          if (isNaN(num) || num < 0) {
            entry.warnings.push(`Invalid MRP: "${strVal}"`);
          } else {
            entry.mrpPaise = Math.round(num * 100);
          }
          break;
        }
        case "marginPercent": {
          if (strVal === "") break;
          const cleaned = strVal.replace(/[₹%,]|rs\.?|\/kg/gi, "").trim();
          const num = parseFloat(cleaned);
          if (isNaN(num) || num < 0) {
            entry.warnings.push(`Invalid Margin per KG: "${strVal}"`);
          } else {
            entry.marginPercent = parseFloat(num.toFixed(2));
            hasExplicitMargin = true;
          }
          break;
        }
        case "gstPercent": {
          if (strVal === "") break;
          const cleaned = strVal.replace(/[%₹,\s]/g, "").trim();
          const num = parseFloat(cleaned);
          if (isNaN(num) || num < 0 || num > 100) {
            entry.warnings.push(`Invalid GST %: "${strVal}", defaulting to 0%`);
          } else {
            entry.gstPercent = parseFloat(num.toFixed(2));
            hasExplicitGst = true;
          }
          break;
        }
        case "availableQty": {
          if (strVal === "") break;
          const num = parseInt(strVal, 10);
          if (isNaN(num) || num < 0) {
            entry.errors.push(`Invalid stock qty: "${strVal}"`);
          } else {
            entry.availableQty = num;
          }
          break;
        }
        case "brand":
          entry.brand = strVal || undefined;
          break;
        case "description":
          entry.description = strVal || undefined;
          break;
        case "origin":
          entry.origin = strVal || undefined;
          break;
        case "packagingType":
          entry.packagingType = strVal || undefined;
          break;
        case "shelfLife":
          entry.shelfLife = strVal || undefined;
          break;
        case "moq": {
          if (strVal === "") break;
          const num = parseInt(strVal, 10);
          if (!isNaN(num) && num > 0) {
            entry.moq = num;
          }
          break;
        }
      }
    }

    if (!hasExplicitGst) {
      entry.gstPercent = 0;
    }

    if (!hasExplicitWeight) {
      entry.weightKg = inferWeightKg(entry.name, entry.unit);
    }

    if (!hasExplicitMargin) {
      entry.marginPercent = 0;
    }

    // Selling Price (Price On App) = (Weight * Margin per KG) + NIP
    if (hasExplicitNip && entry.nipPaise > 0) {
      entry.pricePaise = calculateSellingPricePaise(
        entry.nipPaise,
        entry.weightKg,
        entry.marginPercent
      );
    } else if (hasExplicitPrice && entry.pricePaise > 0) {
      entry.nipPaise = calculateNipPaise(
        entry.pricePaise,
        entry.weightKg,
        entry.marginPercent
      );
    }

    // Required field validations — SKU Code is auto-generated if omitted
    if (!entry.code || !entry.code.trim()) {
      const catSlug = (entry.category || "GEN").replace(/[^a-zA-Z0-9]/g, "").slice(0, 3).toUpperCase() || "GEN";
      const nameSlug = (entry.name || "SKU").replace(/[^a-zA-Z0-9]/g, "").slice(0, 4).toUpperCase() || "SKU";
      const randomSuffix = Math.random().toString(36).substring(2, 6).toUpperCase();
      entry.code = `MDD-${catSlug}-${nameSlug}-${randomSuffix}`;
    }
    if (!entry.name) entry.errors.push("Product Name is required.");
    if (entry.pricePaise <= 0 && entry.nipPaise <= 0 && entry.errors.length === 0) {
      entry.errors.push("NIP (Landing Price) or Selling Price must be greater than 0.");
    }

    // Note: MRP (entry.mrpPaise) is completely independent and is NEVER calculated from NIP/Price/Margin/GST.

    parsed.push(entry);
  }

  return parsed;
}

// ---- Validate ----

/**
 * Validates parsed rows for duplicates and data integrity.
 */
export function validateParsedRows(
  rows: ParsedSkuRow[]
): ValidationResult {
  const result: ValidationResult = {
    totalRows: rows.length,
    validRows: 0,
    errorRows: 0,
    warningRows: 0,
    errors: [],
    warnings: [],
  };

  // Check for duplicate codes
  const codeCounts = new Map<string, number[]>();
  rows.forEach((row) => {
    const key = row.code.toLowerCase();
    if (!codeCounts.has(key)) codeCounts.set(key, []);
    codeCounts.get(key)!.push(row.rowNum);
  });

  codeCounts.forEach((rowNums, code) => {
    if (rowNums.length > 1) {
      rowNums.forEach((rn) => {
        const row = rows.find((r) => r.rowNum === rn);
        if (row) {
          row.warnings.push(
            `Duplicate SKU code "${code}" (also on rows ${rowNums.filter((n) => n !== rn).join(", ")})`
          );
        }
      });
    }
  });

  // Aggregate results
  rows.forEach((row) => {
    if (row.errors.length > 0) {
      result.errorRows++;
      row.errors.forEach((e) =>
        result.errors.push({ row: row.rowNum, message: e })
      );
    } else {
      result.validRows++;
    }
    if (row.warnings.length > 0) {
      result.warningRows++;
      row.warnings.forEach((w) =>
        result.warnings.push({ row: row.rowNum, message: w })
      );
    }
  });

  return result;
}

// ---- Download Helper ----

/**
 * Triggers a browser download for a Blob.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}
